import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, KeyRound, ArrowLeft, ArrowRight, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { setToken, isAuthed } from "@/lib/consult/authSession";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Preview / localhost only — hidden on production (hepcure.ca).
const isPreview = (() => {
  if (typeof window === "undefined") return false;
  const h = (window.location.hostname || "").toLowerCase();
  return !/(^|\.)hepcure\.ca$/.test(h);
})();

const DARK_GREEN = "#0F3B2E";
const DARK_GREEN_HOVER = "#0A2A20";
const SAGE = "#9DB5A8";
const SAGE_HOVER = "#8AA697";
const LABEL_GREEN = "#2D5A4C";
const TEXT = "#1C2B26";
const MUTED = "#4A5D55";
const BG = "#F9F8F6";
const BORDER = "#E5E2DC";

const NEXT_PATH = "/outreach/dashboard";

export default function ConsultAuthGate() {
  const nav = useNavigate();
  const location = useLocation();
  // keep ?preview_staff=… through the login redirect (staff-scope preview).
  // NOTE: reads the router location — App.js masks window.location to "/".
  const nextPath = () => NEXT_PATH + (location.search || "");
  const [step, setStep] = useState(1);
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [session, setSession] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isAuthed()) nav(nextPath(), { replace: true });
  }, [nav]);

  const submitPin = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setBusy(true);
    try {
      // 1) Try DB-backed multi-user PIN (single-step login).
      //    Grants a token if the PIN belongs to an active user with
      //    the "consultations" permission.
      try {
        const { data: userRes } = await axios.post(
          `${BACKEND}/api/pp/auth/user-pin`,
          { pin, section: "consultations" },
        );
        if (userRes.valid && userRes.token) {
          setToken(userRes.token);
          nav(nextPath(), { replace: true });
          return;
        }
      } catch (_) {
        // fall through to legacy 2-step flow
      }
      // 2) Legacy shared PIN + password flow (kept as admin backup)
      const { data } = await axios.post(`${BACKEND}/api/pp/auth/verify-pin`, { pin });
      if (data.valid) {
        setSession(data.session);
        setStep(2);
      } else {
        toast.error("Incorrect PIN");
        setPin("");
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/pp/auth/verify-password`, {
        session,
        password,
      });
      if (data.valid) {
        setToken(data.token);
        nav(nextPath(), { replace: true });
      } else {
        toast.error("Incorrect password");
        setPassword("");
      }
    } catch {
      // Session expired (backend restart or stale tab) — send back to step 1.
      toast.error("Session expired — re-enter your PIN");
      setPassword("");
      setSession("");
      setStep(1);
    } finally {
      setBusy(false);
    }
  };

  const skipAuth = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: pinRes } = await axios.post(
        `${BACKEND}/api/pp/auth/verify-pin`,
        { pin: "022024" },
      );
      if (!pinRes.valid || !pinRes.session) throw new Error("PIN failed");
      const { data: pwRes } = await axios.post(
        `${BACKEND}/api/pp/auth/verify-password`,
        { session: pinRes.session, password: "Olivier2024" },
      );
      if (!pwRes.valid || !pwRes.token) throw new Error("Password failed");
      setToken(pwRes.token);
      nav(nextPath(), { replace: true });
    } catch {
      toast.error("Skip failed — try entering your PIN manually");
    } finally {
      setBusy(false);
    }
  };

  const btnStyle = (filled) => ({
    height: 52,
    borderRadius: 10,
    background: filled ? DARK_GREEN : SAGE,
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: 600,
    width: "100%",
    border: "none",
    cursor: filled && !busy ? "pointer" : "default",
    transition: "background-color .18s ease",
  });

  const inputBase = {
    width: "100%",
    height: 52,
    borderRadius: 10,
    padding: "0 16px",
    fontSize: 18,
    background: "#FFFFFF",
    border: `1px solid ${BORDER}`,
    outline: "none",
    color: TEXT,
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    boxSizing: "border-box",
  };

  return (
    <div
      data-testid="consult-auth-gate"
      style={{
        minHeight: "100vh",
        background: BG,
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        padding: "0 20px",
      }}
    >
      <div style={{ maxWidth: 440, margin: "0 auto", paddingTop: "26vh" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: LABEL_GREEN,
            marginBottom: 18,
          }}
        >
          <Lock style={{ width: 15, height: 15 }} strokeWidth={1.8} />
          <span
            style={{
              fontSize: 13,
              letterSpacing: "0.28em",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            Security Verification
          </span>
        </div>

        {step === 1 && (
          <form onSubmit={submitPin}>
            <h1
              style={{
                fontFamily: "'Work Sans', system-ui, sans-serif",
                fontSize: 26,
                fontWeight: 700,
                color: TEXT,
                lineHeight: 1.2,
                margin: "0 0 8px",
                letterSpacing: "-0.01em",
              }}
            >
              Enter your PIN
            </h1>
            <p
              style={{
                fontSize: 14,
                color: MUTED,
                lineHeight: 1.4,
                margin: "0 0 24px",
              }}
            >
              A 6-digit access code is required.
            </p>

            <label
              htmlFor="consult-pin"
              style={{
                display: "block",
                fontSize: 13,
                letterSpacing: "0.24em",
                fontWeight: 600,
                textTransform: "uppercase",
                color: LABEL_GREEN,
                marginBottom: 10,
              }}
            >
              Access PIN
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="consult-pin"
                autoFocus
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                data-testid="consult-auth-pin-input"
                style={{
                  ...inputBase,
                  letterSpacing: pin ? "0.35em" : "normal",
                  fontWeight: 700,
                  paddingRight: 48,
                }}
              />
              <button
                type="button"
                onClick={() => setShowPin((v) => !v)}
                data-testid="consult-auth-pin-toggle"
                aria-label={showPin ? "Hide PIN" : "Show PIN"}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  padding: 8,
                  cursor: "pointer",
                  color: MUTED,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {showPin ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
              </button>
            </div>

            <div style={{ height: 22 }} />

            <button
              type="submit"
              disabled={busy || !pin}
              data-testid="consult-auth-pin-submit"
              style={btnStyle(!!pin && !busy)}
              onMouseEnter={(e) => {
                if (pin && !busy) e.currentTarget.style.background = DARK_GREEN_HOVER;
                else e.currentTarget.style.background = SAGE_HOVER;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = pin && !busy ? DARK_GREEN : SAGE;
              }}
            >
              {busy ? "Verifying…" : "Continue"}
            </button>

            {isPreview && (
              <button
                type="button"
                onClick={skipAuth}
                disabled={busy}
                data-testid="consult-auth-skip"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: LABEL_GREEN,
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  marginTop: 14,
                  cursor: busy ? "default" : "pointer",
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  opacity: busy ? 0.5 : 1,
                }}
              >
                Skip step
                <ArrowRight style={{ width: 15, height: 15 }} strokeWidth={2} />
              </button>
            )}
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitPassword}>
            <h1
              style={{
                fontFamily: "'Work Sans', system-ui, sans-serif",
                fontSize: 26,
                fontWeight: 700,
                color: TEXT,
                lineHeight: 1.2,
                margin: "0 0 8px",
                letterSpacing: "-0.01em",
              }}
            >
              Security password
            </h1>
            <p
              style={{
                fontSize: 14,
                color: MUTED,
                lineHeight: 1.4,
                margin: "0 0 24px",
              }}
            >
              One more check to confirm it&rsquo;s really you.
            </p>

            <label
              htmlFor="consult-password"
              style={{
                display: "block",
                fontSize: 13,
                letterSpacing: "0.24em",
                fontWeight: 600,
                textTransform: "uppercase",
                color: LABEL_GREEN,
                marginBottom: 10,
              }}
            >
              Security password
            </label>

            <div
              style={{
                position: "relative",
                borderRadius: 10,
                border: `1.5px solid ${DARK_GREEN}`,
                background: "#FFFFFF",
                height: 52,
                display: "flex",
                alignItems: "center",
              }}
            >
              <KeyRound
                style={{ width: 18, height: 18, color: TEXT, marginLeft: 16, flexShrink: 0 }}
                strokeWidth={1.8}
              />
              <input
                id="consult-password"
                autoFocus
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="consult-auth-password-input"
                placeholder="Enter password"
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  paddingLeft: 12,
                  paddingRight: 8,
                  fontSize: 17,
                  color: TEXT,
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="consult-auth-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 12,
                  marginRight: 4,
                  cursor: "pointer",
                  color: MUTED,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {showPassword ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
              </button>
            </div>

            <div style={{ height: 22 }} />

            <button
              type="submit"
              disabled={busy || !password}
              data-testid="consult-auth-password-submit"
              style={btnStyle(!!password && !busy)}
              onMouseEnter={(e) => {
                if (password && !busy) e.currentTarget.style.background = DARK_GREEN_HOVER;
                else e.currentTarget.style.background = SAGE_HOVER;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = password && !busy ? DARK_GREEN : SAGE;
              }}
            >
              {busy ? "Verifying…" : "Unlock"}
            </button>

            {isPreview && (
              <button
                type="button"
                onClick={skipAuth}
                disabled={busy}
                data-testid="consult-auth-skip-step2"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: LABEL_GREEN,
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  marginTop: 14,
                  cursor: busy ? "default" : "pointer",
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  opacity: busy ? 0.5 : 1,
                }}
              >
                Skip step
                <ArrowRight style={{ width: 15, height: 15 }} strokeWidth={2} />
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setPassword("");
                setStep(1);
              }}
              data-testid="consult-auth-back-to-pin"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                width: "100%",
                background: "transparent",
                border: "none",
                color: LABEL_GREEN,
                fontSize: 15,
                fontWeight: 500,
                marginTop: 18,
                cursor: "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              <ArrowLeft style={{ width: 15, height: 15 }} strokeWidth={2} />
              Back to PIN
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
